/**
 * Onboarding analytics API service for SMS admin dashboard.
 * ADR 085 Phase 5: Admin visibility into onboarding completion rates.
 *
 * Migrated to typed `@tiaki/http-client` per ADR 113 §Decision.
 */

import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

// --- Types ---

export interface StepStats {
  completed: number;
  skipped: number;
  pending: number;
  completion_rate: number;
}

export interface PlatformTroubleshooting {
  platform: string;
  sessions: number;
  resolved: number;
  escalated: number;
  avg_turns: number | null;
}

export interface ReengagementStats {
  event_type: string;
  sent: number;
  converted: number;
}

export interface OnboardingAnalytics {
  total_users: number;
  onboarding_summary: {
    completed: number;
    skipped: number;
    in_progress: number;
    not_started: number;
    completion_rate: number;
  };
  step_stats: Record<string, StepStats>;
  platform_troubleshooting: PlatformTroubleshooting[];
  reengagement: ReengagementStats[];
}

export interface UserStepStatus {
  id: string;
  label: string;
  status: 'completed' | 'skipped' | 'pending';
  completed_at: string | null;
  skipped_at: string | null;
}

export interface UserOnboardingStatus {
  id: number;
  name: string;
  email: string;
  status: 'completed' | 'skipped' | 'in_progress' | 'not_started';
  completed_at: string | null;
  steps: UserStepStatus[];
}

export interface UserStatusResponse {
  page: number;
  page_size: number;
  total: number;
  results: UserOnboardingStatus[];
}

export interface UserStatusFilters {
  school_id?: number;
  status?: string;
  step?: string;
  page?: number;
  page_size?: number;
  search?: string;
}

// --- API calls ---

export async function getOnboardingAnalytics(
  schoolId?: number,
): Promise<OnboardingAnalytics> {
  const result = await client.GET('/api/v2/onboarding/analytics/', {
    params: {
      query: (schoolId ? { school_id: schoolId } : {}) as never,
    },
  });
  return unwrap(result) as unknown as OnboardingAnalytics;
}

export async function getOnboardingUserStatus(
  filters: UserStatusFilters = {},
): Promise<UserStatusResponse> {
  const result = await client.GET('/api/v2/onboarding/analytics/users/', {
    params: { query: filters as never },
  });
  return unwrap(result) as unknown as UserStatusResponse;
}
